import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
} from "@tanstack/react-table";
import React, { useContext, useState } from "react";
import { ScheduleSettlementRowObj } from "..";
import ScheduleSettlement from "./ScheduleSettlement";
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverHeader,
  PopoverArrow,
} from "@chakra-ui/react";
import { Portal } from "@chakra-ui/portal";
import { BsArrowUp, BsArrowDown } from "react-icons/bs";
import Pagination from "components/pagination";
import toast from "react-hot-toast";
import { RiArrowUpDownFill } from "react-icons/ri";
import SettlementDetails from "./SettlementDetails"
import SettlementLogs from "./SettlementLogs"
import StarteSettlement from "./StarteSettlement"
import PauseSettlement from "./PauseSettlement"
import DeleteSettlement from "./DeleteSettlement"

const columnHelper = createColumnHelper<ScheduleSettlementRowObj>();

function ScheduleSettlementTable(props:{
    allOrgDetails: any;
    page: number;
    setPage: any;
    totalpage: number;
    totalItems: number;
    currentPage: number;
    pageSize: number;
    setPageSize: any;
    roleData: any;
    fetchAllsettlement: () => void;
    isLoading: boolean;
    tableData: any;
}) {

    const { 
        allOrgDetails , page, setPage, currentPage, totalpage, totalItems, pageSize, setPageSize,
        roleData,fetchAllsettlement,isLoading,tableData
    } = props; 

    let defaultData = tableData;
    const [data, setData] = React.useState(() => [...defaultData]);
    const [sorting, setSorting] = React.useState<SortingState>([]);

    const avatarColors = [
        {
        bg: "linear-gradient(135deg,#f0fdf4,#dcfce7)",
        border: "#bbf7d0",
        color: "#15803d",
        },
        {
        bg: "linear-gradient(135deg,#eff6ff,#dbeafe)",
        border: "#bfdbfe",
        color: "#2563eb",
        },
        {
        bg: "linear-gradient(135deg,#faf5ff,#ede9fe)",
        border: "#ddd6fe",
        color: "#7c3aed",
        },
        {
        bg: "linear-gradient(135deg,#fffbeb,#fef3c7)",
        border: "#fde68a",
        color: "#d97706",
        },
        {
        bg: "linear-gradient(135deg,#fff1f2,#ffe4e6)",
        border: "#fecdd3",
        color: "#e11d48",
        },
    ];
    const getAvatarStyle = (index: number) => avatarColors[index % avatarColors.length];

    const schedulePermissions = roleData?.[0]?.schedule_settlement?.value;
    const hasActionPermission =
        schedulePermissions?.view_schedule_settlement_details ||
        schedulePermissions?.view_schedule_settlement_logs ||
        schedulePermissions?.update_schedule_settlement ||
        schedulePermissions?.start_schedule_settlement ||
        schedulePermissions?.delete_schedule_settlement;
        
    const columns = [
        columnHelper.accessor("organization_name", {
          id: "organization_name",
          header: () => <span className="st-th">Organization</span>,
          cell: (info: any) => {
            const style = getAvatarStyle(info.row.index);
            return (
              <div className="st-name-cell">
                <div
                  className="st-org-avatar"
                  style={{
                    background: style.bg,
                    border: `1.5px solid ${style.border}`,
                    color: style.color,
                  }}
                >
                  {info.row.original?.organization_name
                    ?.charAt(0)
                    ?.toUpperCase() || "O"}
                </div>
                <span className="st-name">
                  {info.row.original?.organization_name}
                </span>
              </div>
            );
          },
          sortingFn: (rowA, rowB) => {
            const nameA = rowA.original.organization_name?.name?.toLowerCase() || "";
            const nameB = rowB.original.organization_name?.name?.toLowerCase() || "";
            return nameA.localeCompare(nameB);
          },
          enableSorting: true,
        }),
        columnHelper.accessor("clients", {
            id: "clients",
            header: () => <span className="st-th">clients</span>,
            cell: (info: any) => (
                <span className="st-cell-text">
                {info?.row.original?.clients
                    ?.map((m: any) => m?.name)
                    .join(", ")}
                </span>
            ),
            sortingFn: (rowA, rowB) => {
                const nameA =
                rowA.original.clients
                    ?.map((m: any) => m?.name)
                    .join(", ")
                    .toLowerCase() || "";
                const nameB =
                rowB.original.clients
                    ?.map((m: any) => m?.name)
                    .join(", ")
                    .toLowerCase() || "";
                return nameA.localeCompare(nameB);
            },
            enableSorting: true,
        }),
        columnHelper.accessor("created_at", {
          id: "created_at",
          header: () => <span className="st-th">Created Date</span>,
          cell: (info: any) => (
            <span className="st-date">{info.row.original?.created_at}</span>
          ),
          sortingFn: (rowA, rowB) =>
            new Date(rowA.original.created_at).getTime() -
            new Date(rowB.original.created_at).getTime(),
          enableSorting: true,
        }),
        columnHelper.accessor("schedule_type", {
            id: "schedule_type",
            header: () => <span className="st-th">Schedule Type</span>,
            cell: (info: any) => {
                const {
                    schedule_type,
                    execution_time,
                    execution_day,
                } = info.row.original;

                let subtitle = "";

                switch ((schedule_type || "").toLowerCase()) {
                    case "daily":
                        subtitle = `Every day at ${execution_time}`;
                        break;

                    case "weekly":
                        subtitle = `Every ${execution_day} at ${execution_time}`;
                        break;

                    case "monthly":
                        subtitle = execution_day
                            ? `Every month on ${execution_day} at ${execution_time}`
                            : `Every month at ${execution_time}`;
                        break;

                    default:
                        subtitle = execution_time;
                }

                return (
                    <div className="st-schedule-cell">
                        <div className="st-payout capitalize">
                            {schedule_type}
                        </div>

                        <div className="st-schedule-sub">
                            {subtitle}
                        </div>
                    </div>
                );
            },
        }),
        columnHelper.accessor("total_settlement_generated", {
            id: "total_settlement_generated",
            header: () => <span className="st-th">Total Settlements</span>,
            cell: (info: any) => (
                <span className="st-cell-text capitalize">
                    {info.row.original?.total_settlement_generated}
                </span>
            ),
        }),
        columnHelper.accessor("status", {
            id: "status",
            header: () => <span className="st-th">Status</span>,
            cell: (info: any) => {
                const active = info?.row?.original?.status === "Active";
                return (
                <span
                    className={`st-status-pill ${
                    active ? "st-status-pill--green" : "st-status-pill--amber"
                    }`}
                >
                    <span
                    className={`st-status-dot ${
                        active
                        ? "st-status-dot--green"
                        : "st-status-dot--amber"
                    }`}
                    />
                    {active ? "Active" : "Paused"}
                </span>
                );
            },
        }),
        ...(hasActionPermission ? [
            columnHelper.accessor("id", {
                id: "id",
                header: () => <span className="st-th">Actions</span>,
                cell: (info: any) => {
                    return (
                        <div className="st-actions">
                            {roleData?.[0]?.schedule_settlement?.value?.view_schedule_settlement_details && (
                                <SettlementDetails info={info.row.original} />
                            )}
                            {roleData?.[0]?.schedule_settlement?.value?.view_schedule_settlement_logs && (
                                <SettlementLogs info={info.row.original} />
                            )}
                            {roleData?.[0]?.schedule_settlement?.value?.update_schedule_settlement && (
                                <ScheduleSettlement allOrgDetails={allOrgDetails} fetchAllsettlement={fetchAllsettlement} id={info.row.original.id}/>
                            )}
                            {
                                info?.row?.original?.status === "Active" ? (
                                    roleData?.[0]?.schedule_settlement?.value?.start_schedule_settlement ? (
                                        <PauseSettlement
                                            id={info.row.original.id}
                                            fetchAllsettlement={fetchAllsettlement}
                                        />
                                    ) : null
                                ) : (
                                    roleData?.[0]?.schedule_settlement?.value?.start_schedule_settlement ? (
                                        <StarteSettlement
                                            id={info.row.original.id}
                                            fetchAllsettlement={fetchAllsettlement}
                                        />
                                    ) : null
                                )
                            }      
                            {(info?.row?.original?.status === "Pause" && roleData?.[0]?.schedule_settlement?.value?.delete_schedule_settlement) && (     
                                <DeleteSettlement id={info.row.original.id} fetchAllsettlement={fetchAllsettlement}/>
                            )}
                        </div>    
                    )
                },
                enableSorting: false
            })
        ] : []),
    ];

    React.useEffect(() => {
        setData(tableData);
    }, [tableData]);

    const table = useReactTable({
        data,
        columns:columns,
        state: { sorting },
        onSortingChange: setSorting,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
    });

    return (
        <div className="st-card">
            <div className="st-header">
                <div className="st-header-left">
                    <div className="st-header-icon">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="2" y="5" width="20" height="14" rx="2" />
                        <line x1="2" y1="10" x2="22" y2="10" />
                    </svg>
                    </div>
                    <div>
                    <h3 className="st-title">Schedule Settlement List</h3>
                    <p className="st-subtitle">
                        {totalItems > 0
                        ? `${totalItems} total record${totalItems !== 1 ? "s" : ""}`
                        : "No records found"}
                    </p>
                    </div>
                </div>
                <div className="st-header-right">
                    <div className="st-search-wrap">
                    {roleData?.[0]?.schedule_settlement?.value?.create_schedule_settlement && (
                        <ScheduleSettlement
                            allOrgDetails={allOrgDetails}
                            fetchAllsettlement={fetchAllsettlement}
                        />
                    )}
                    </div>
                </div>
            </div>
            <div className="st-table-wrap">
                {props.isLoading ? (
                    <div className="st-loading">
                    <div className="st-spinner" />
                    <p className="st-loading-txt">Loading scheduled settlements…</p>
                    </div>
                ) : (
                    <table className="st-table">
                    <thead>
                        {table.getHeaderGroups().map((hg) => (
                        <tr key={hg.id}>
                            {hg.headers.map((h) => (
                            <th
                                key={h.id}
                                colSpan={h.colSpan}
                                onClick={h.column.getToggleSortingHandler()}
                                className="st-thead-th"
                            >
                                <div className="st-th-inner">
                                {flexRender(
                                    h.column.columnDef.header,
                                    h.getContext()
                                )}
                                {h.column.getCanSort() && (
                                    <span className="st-sort-icon">
                                    {h.column.getIsSorted() === "asc" ? (
                                        <BsArrowUp size={11} />
                                    ) : h.column.getIsSorted() === "desc" ? (
                                        <BsArrowDown size={11} />
                                    ) : (
                                        <RiArrowUpDownFill size={11} />
                                    )}
                                    </span>
                                )}
                                </div>
                            </th>
                            ))}
                        </tr>
                        ))}
                    </thead>
                    <tbody>
                        {table.getRowModel().rows.length > 0 ? (
                        table.getRowModel().rows.map((row, ri) => (
                            <tr
                            key={row.id}
                            className={`st-tbody-tr${
                                ri % 2 !== 0 ? " st-tbody-tr--alt" : ""
                            }`}
                            >
                            {row.getVisibleCells().map((cell) => (
                                <td key={cell.id} className="st-tbody-td">
                                {flexRender(
                                    cell.column.columnDef.cell,
                                    cell.getContext()
                                )}
                                </td>
                            ))}
                            </tr>
                        ))
                        ) : (
                        <tr>
                            <td colSpan={8}>
                            <div className="st-empty">
                                <div className="st-empty-icon">
                                <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                                    <rect x="2" y="5" width="20" height="14" rx="2" />
                                    <line x1="2" y1="10" x2="22" y2="10" />
                                </svg>
                                </div>
                                <p className="st-empty-title">No scheduled settlements found</p>
                            </div>
                            </td>
                        </tr>
                        )}
                    </tbody>
                    </table>
                )}
            </div>
            <div className="st-pagination-wrap">
                <Pagination
                    setPage={setPage}
                    page={page}
                    totalpage={totalpage}
                    currentPage={currentPage}
                    pageSize={pageSize}
                    setPageSize={setPageSize}
                    arraySize={[50, 100, 200]}
                />
            </div>
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');
                .st-schedule-cell{
                    display:flex;
                    flex-direction:column;
                    gap:2px;
                }
                .st-actions { display: flex; align-items: center; gap: 6px; flex-wrap: wrap; }
                .st-schedule-sub{
                    font-size:12px;
                    color:#64748b;
                    font-weight:500;
                    line-height:16px;
                }
                    
                /* ── Card ── */
                .st-card {
                font-family: 'Plus Jakarta Sans', sans-serif;
                background: #ffffff;
                border: 1px solid #e2e8f0;
                border-radius: 18px;
                overflow: hidden;
                box-shadow: 0 2px 8px rgba(15,23,42,0.06);
                }

                /* ── Header ── */
                .st-header {
                padding: 20px 24px 16px;
                border-bottom: 1px solid #f1f5f9;
                display: flex; align-items: center; justify-content: space-between; gap: 14px; flex-wrap: wrap;
                }
                .st-header-left { display: flex; align-items: center; gap: 12px; }
                .st-header-right { display: flex; align-items: center; gap: 12px; margin-left: auto; }
                .st-header-icon {
                width: 40px; height: 40px; border-radius: 11px;
                background: linear-gradient(135deg, #eff6ff, #dbeafe);
                border: 1.5px solid #bfdbfe;
                display: flex; align-items: center; justify-content: center;
                color: #2563eb; flex-shrink: 0;
                }
                .st-title { font-size: 15px; font-weight: 700; color: #0f172a; letter-spacing: -0.2px; margin-bottom: 2px; line-height: 22px; }
                .st-subtitle { font-size: 12px; color: #94a3b8; font-weight: 500; }
                .st-search-wrap { display:flex; align-items:center; gap:8px; flex-wrap:wrap; }
                .smt-top-glow {
                height: 3px;
                background: linear-gradient(90deg,#0f2744,#2563eb,#7c3aed,#2563eb,#0f2744);
                background-size: 200% 100%;
                animation: smtGlow 3s linear infinite; flex-shrink: 0;
                }
                @keyframes smtGlow { 0%{background-position:0%} 100%{background-position:200%} }

                .prof-glow { height: 3px; flex-shrink: 0; background-size: 200% 100%; animation: profGlow 3s linear infinite; }
                .prof-glow--red    { background: linear-gradient(90deg,#7f1d1d,#dc2626,#ef4444,#dc2626,#7f1d1d); }
                @keyframes profGlow { 0%{background-position:0%} 100%{background-position:200%} }
                
                /* ── Download notice ── */
                .st-download-notice {
                display: flex; align-items: center; gap: 8px;
                margin: 0 24px 0; padding: 10px 14px;
                background: #eff6ff; border: 1px solid #bfdbfe;
                border-radius: 10px; font-size: 12.5px; font-weight: 600; color: #2563eb;
                }

                /* ── Table container ── */
                .st-table-wrap { overflow-x: auto; }
                .st-table-wrap::-webkit-scrollbar { height: 4px; }
                .st-table-wrap::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 4px; }
                .st-table { width: 100%; border-collapse: collapse; }

                /* ── Head ── */
                .st-thead-th {
                padding: 12px 16px 10px;
                background: #f8fafc;
                border-bottom: 1.5px solid #e2e8f0;
                text-align: left; cursor: pointer; white-space: nowrap; user-select: none;
                }
                .st-thead-th:hover { background: #f1f5f9; }
                .st-th-inner { display: flex; align-items: center; gap: 5px; }
                .st-th { font-size: 11.5px; font-weight: 700; color: #374151; text-transform: uppercase; letter-spacing: 0.5px; }
                .st-sort-icon { color: #94a3b8; display: flex; align-items: center; transition: color 0.15s; }
                .st-thead-th:hover .st-sort-icon { color: #2563eb; }

                /* ── Rows ── */
                .st-tbody-tr { transition: background 0.12s; }
                .st-tbody-tr:hover { background: #f8fafc; }
                .st-tbody-tr--alt { background: #fafbfc; }
                .st-tbody-tr--alt:hover { background: #f1f5f9; }
                .st-tbody-td { padding: 13px 16px; border-bottom: 1px solid #f1f5f9; vertical-align: middle; }

                /* ── Cells ── */
                .st-name-cell { display: flex; align-items: center; gap: 9px; }
                .st-org-avatar {
                width: 30px; height: 30px; border-radius: 8px; flex-shrink: 0;
                display: flex; align-items: center; justify-content: center;
                font-size: 12px; font-weight: 700;
                }
                .st-name { font-size: 13px; font-weight: 600; color: #0f172a; white-space: nowrap; }
                .st-cell-text { font-size: 13px; font-weight: 500; color: #374151; }
                .st-date { font-size: 13px; color: #64748b; font-weight: 500; white-space: nowrap; }
                .st-payout { font-size: 13px; font-weight: 700; color: #0f172a; }
                .st-dash { color: #cbd5e1; font-weight: 400; }

                /* ── Status pills ── */
                .st-status-pill {
                display: inline-flex; align-items: center; gap: 6px;
                padding: 5px 11px; border-radius: 8px;
                font-size: 12px; font-weight: 700; white-space: nowrap;
                }
                .st-status-pill--green {
                background: linear-gradient(135deg,#f0fdf4,#dcfce7);
                border: 1.5px solid #bbf7d0; color: #15803d;
                }
                .st-status-pill--red {
                background: linear-gradient(135deg,#fff1f2,#ffe4e6);
                border: 1.5px solid #fecdd3; color: #be123c;
                }
                .st-status-pill--amber {
                background: linear-gradient(135deg,#fffbeb,#fef3c7);
                border: 1.5px solid #fde68a; color: #b45309;
                }
                .st-status-dot { width: 6px; height: 6px; border-radius: 50%; flex-shrink: 0; }
                .st-status-dot--green { background: #16a34a; }
                .st-status-dot--red { background: #e11d48; }
                .st-status-dot--amber { background: #d97706; }

                /* ── Actions ── */
                .st-actions { display: flex; align-items: center; gap: 6px; flex-wrap: wrap;}
                .st-action-btn {
                width: 32px; height: 32px; border-radius: 8px;
                display: flex; align-items: center; justify-content: center;
                cursor: pointer; transition: all .15s; border: 1.5px solid;
                }
                .st-action-btn--blue {
                background: #eff6ff; border-color: #bfdbfe; color: #2563eb;
                }
                .st-action-btn--blue:hover { background: #dbeafe; border-color: #93c5fd; }
                .st-action-btn--blue-solid {
                background: #dbeafe; border-color: #93c5fd; color: #1d4ed8;
                opacity: 0.7; cursor: default;
                }
                .st-action-btn--red {
                background: #fff7f7; border-color: #fecaca; color: #dc2626;
                }
                .st-action-btn--red:hover { background: #fee2e2; border-color: #fca5a5; }

                /* ── Empty ── */
                .st-empty {
                display: flex; flex-direction: column; align-items: center;
                justify-content: center; padding: 56px 24px; gap: 10px;
                }
                .st-empty-icon {
                width: 52px; height: 52px; border-radius: 13px;
                background: #f8fafc; border: 1.5px solid #e2e8f0;
                display: flex; align-items: center; justify-content: center; color: #cbd5e1;
                }
                .st-empty-title { font-size: 14px; font-weight: 700; color: #64748b; }
                .st-empty-sub { font-size: 12.5px; color: #94a3b8; font-weight: 500; text-align: center; }

                /* ── Loading ── */
                .st-loading {
                display: flex; flex-direction: column; align-items: center;
                justify-content: center; padding: 56px; gap: 12px;
                }
                .st-spinner {
                width: 28px; height: 28px;
                border: 3px solid #e2e8f0; border-top-color: #2563eb;
                border-radius: 50%; animation: stSpin 0.7s linear infinite;
                }
                @keyframes stSpin { to { transform: rotate(360deg); } }
                .st-loading-txt { font-size: 13px; color: #94a3b8; font-weight: 500; }

                /* ── Pagination ── */
                .st-pagination-wrap { padding: 12px 16px; border-top: 1px solid #f1f5f9; background: #fafbfc; }

                /* ══════════════════════════════
                DELETE MODAL PANEL
                ══════════════════════════════ */
                .sdm-panel {
                font-family: 'Plus Jakarta Sans', sans-serif;
                position: fixed; top: 0; right: 0; bottom: 0;
                width: 100%; max-width: 550px;
                background: #ffffff;
                z-index: 1004;
                display: flex; flex-direction: column;
                box-shadow: -8px 0 40px rgba(15,23,42,0.15);
                transition: transform .32s cubic-bezier(0.16,1,0.3,1);
                }
                .sdm-panel--open  { transform: translateX(0); }
                .sdm-panel--close { transform: translateX(100%); }

                .sdm-header {
                padding: 15px 28px;
                background: linear-gradient(135deg,#7f1d1d 0%,#b91c1c 60%,#dc2626 100%);
                display: flex; align-items: center; justify-content: space-between; flex-shrink: 0;
                }
                .sdm-header-left { display: flex; align-items: center; gap: 14px; }
                .sdm-header-icon {
                width: 42px; height: 42px; border-radius: 11px;
                background: rgba(255,255,255,.18); border: 1.5px solid rgba(255,255,255,.28);
                display: flex; align-items: center; justify-content: center;
                color: #fff; flex-shrink: 0;
                }
                .sdm-header-title { font-size: 16px; font-weight: 800; color: #fff; letter-spacing: -.3px; margin-bottom: 2px; line-height: 22px; }
                .sdm-header-sub { font-size: 12.5px; color: rgba(255,255,255,.65); font-weight: 500; }
                .sdm-close-btn {
                background: rgba(255,255,255,.15); border: none; border-radius: 8px;
                width: 32px; height: 32px; cursor: pointer;
                display: flex; align-items: center; justify-content: center;
                color: rgba(255,255,255,.8); transition: background .15s;
                }
                .sdm-close-btn:hover { background: rgba(255,255,255,.25); color: #fff; }

                .sdm-body { flex: 1; padding: 28px; display: flex; flex-direction: column; gap: 20px; overflow-y: auto; }
                .sdm-warning-card {
                display: flex; gap: 14px;
                background: linear-gradient(135deg, #fff7f7, #fee2e2);
                border: 1.5px solid #fecaca; border-radius: 14px; padding: 18px;
                }
                .sdm-warning-icon-wrap {
                width: 44px; height: 44px; border-radius: 11px;
                background: #fff; border: 1.5px solid #fecaca;
                display: flex; align-items: center; justify-content: center;
                color: #dc2626; flex-shrink: 0;
                }
                .sdm-warning-content { flex: 1; }
                .sdm-warning-title { font-size: 14px; font-weight: 700; color: #991b1b; margin-bottom: 6px; line-height: 1; }
                .sdm-warning-desc { font-size: 13px; color: #b91c1c; line-height: 1.55; font-weight: 500; }

                .sdm-footer {
                padding: 16px 28px; border-top: 1px solid #f1f5f9; background: #fafbfc;
                display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 12px; flex-shrink: 0;
                }
                .sdm-footer-note { display: flex; align-items: center; gap: 6px; font-size: 12px; color: #94a3b8; font-weight: 500; }
                .sdm-footer-btns { display: flex; gap: 10px; }
                .sdm-btn-cancel {
                font-family: 'Plus Jakarta Sans', sans-serif; font-size: 13.5px; font-weight: 600; color: #64748b;
                padding: 10px 20px; border-radius: 10px; border: 1.5px solid #e2e8f0; background: #fff;
                cursor: pointer; transition: all .15s;
                }
                .sdm-btn-cancel:hover { background: #f1f5f9; border-color: #cbd5e1; color: #374151; }
                .sdm-btn-delete {
                display: inline-flex; align-items: center; gap: 7px;
                font-family: 'Plus Jakarta Sans', sans-serif; font-size: 13.5px; font-weight: 700; color: #fff;
                padding: 10px 22px; border-radius: 10px; border: none;
                background: linear-gradient(135deg, #991b1b, #dc2626);
                cursor: pointer; box-shadow: 0 4px 14px rgba(220,38,38,.3);
                transition: transform .15s, box-shadow .18s;
                }
                .sdm-btn-delete:hover { transform: translateY(-1px); box-shadow: 0 8px 24px rgba(220,38,38,.38); }
                .sdm-btn-delete:disabled { opacity: .5; cursor: not-allowed; transform: none; box-shadow: none; }
                .sdm-spinner {
                display: inline-block; width: 16px; height: 16px;
                border: 2px solid rgba(255,255,255,.35); border-top-color: #fff;
                border-radius: 50%; animation: sdmSpin .65s linear infinite;
                }
                @keyframes sdmSpin { to { transform: rotate(360deg); } }

                /* ══════════════════════════════
                APPROVE MODAL PANEL
                ══════════════════════════════ */
                .sam-panel {
                font-family: 'Plus Jakarta Sans', sans-serif;
                position: fixed; top: 0; right: 0; bottom: 0;
                width: 100%; max-width: 680px;
                background: #ffffff;
                z-index: 1004;
                display: flex; flex-direction: column;
                box-shadow: -8px 0 40px rgba(15,23,42,0.15);
                transition: transform .32s cubic-bezier(0.16,1,0.3,1);
                }
                .sam-panel--open  { transform: translateX(0); }
                .sam-panel--close { transform: translateX(100%); }

                .sam-header {
                padding: 15px 28px;
                background: linear-gradient(135deg,#0f2744 0%,#1a3d6b 60%,#1d4ed8 100%);
                display: flex; align-items: center; justify-content: space-between; flex-shrink: 0;
                }
                .sam-header-left { display: flex; align-items: center; gap: 14px; }
                .sam-header-icon {
                width: 42px; height: 42px; border-radius: 11px;
                background: rgba(255,255,255,.18); border: 1.5px solid rgba(255,255,255,.28);
                display: flex; align-items: center; justify-content: center;
                color: #fff; flex-shrink: 0;
                }
                .sam-header-title { font-size: 15px; font-weight: 800; color: #fff; letter-spacing: -.3px; margin-bottom: 2px; line-height: 22px; }
                .sam-header-sub { font-size: 12px; color: rgba(255,255,255,.65); font-weight: 500; }
                .sam-close-btn {
                background: rgba(255,255,255,.15); border: none; border-radius: 8px;
                width: 32px; height: 32px; cursor: pointer;
                display: flex; align-items: center; justify-content: center;
                color: rgba(255,255,255,.8); transition: background .15s;
                }
                .sam-close-btn:hover { background: rgba(255,255,255,.25); color: #fff; }

                .sam-body {
                flex: 1; padding: 24px 28px; display: flex; flex-direction: column; gap: 20px;
                overflow-y: auto;
                }
                .sam-body::-webkit-scrollbar { width: 4px; }
                .sam-body::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 4px; }

                /* Currency block */
                .sam-currency-block {
                background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 14px;
                padding: 18px; display: flex; flex-direction: column; gap: 14px;
                }
                .sam-currency-badge {
                display: inline-flex; align-items: center;
                padding: 4px 12px; border-radius: 7px;
                background: #1e3a8a; color: #fff;
                font-size: 12px; font-weight: 800; letter-spacing: 0.5px;
                align-self: flex-start;
                }

                /* Summary table */
                .sam-summary-table-wrap { overflow-x: auto; border-radius: 10px; border: 1px solid #e2e8f0; }
                .sam-summary-table { width: 100%; border-collapse: collapse; }
                .sam-summary-th {
                padding: 9px 12px; background: #f1f5f9;
                font-size: 10.5px; font-weight: 700; color: #374151; text-transform: uppercase;
                letter-spacing: 0.4px; text-align: left; white-space: nowrap;
                border-bottom: 1px solid #e2e8f0;
                }
                .sam-summary-td {
                padding: 9px 12px; font-size: 12.5px; font-weight: 600; color: #475569;
                border-bottom: 0; white-space: nowrap;
                }
                .sam-summary-td--highlight { color: #0f172a; font-weight: 700; }

                /* Field rows */
                .sam-field-row { display: flex; gap: 14px; }
                .sam-field-row > * { flex: 1; }
                .sam-field-group { display: flex; flex-direction: column; gap: 5px; }
                .sam-label { font-size: 12.5px; font-weight: 700; color: #374151; }
                .sam-req { color: #dc2626; }
                .sam-input {
                height: 42px; padding: 0 14px;
                border: 1.5px solid #e2e8f0; border-radius: 10px;
                background: #fff; font-family: 'Plus Jakarta Sans', sans-serif;
                font-size: 13px; font-weight: 500; color: #0f172a;
                outline: none; transition: border-color .15s, box-shadow .15s; width: 100%;
                }
                .sam-input:focus { border-color: #2563eb; box-shadow: 0 0 0 3px rgba(37,99,235,0.1); }
                .sam-input--error { border-color: #dc2626 !important; }
                .sam-input--error:focus { box-shadow: 0 0 0 3px rgba(220,38,38,0.1) !important; }
                .sam-input--readonly { background: #f8fafc; color: #64748b; cursor: not-allowed; }
                .sam-input:disabled { background: #f8fafc; color: #94a3b8; cursor: not-allowed; }
                .sam-field-err { font-size: 11.5px; color: #dc2626; font-weight: 600; }

                /* Totals block */
                .sam-totals-block {
                background: linear-gradient(135deg, #f8faff, #eff6ff);
                border: 1.5px solid #bfdbfe; border-radius: 14px;
                padding: 18px; display: flex; flex-direction: column; gap: 14px;
                }
                .sam-totals-badge {
                display: inline-flex; align-items: center;
                padding: 4px 12px; border-radius: 7px;
                background: #2563eb; color: #fff;
                font-size: 12px; font-weight: 800; letter-spacing: 0.5px;
                align-self: flex-start;
                }

                /* Loading state in modal */
                .sam-loading {
                display: flex; flex-direction: column; align-items: center;
                justify-content: center; padding: 60px; gap: 14px;
                }
                .sam-spinner {
                width: 30px; height: 30px;
                border: 3px solid #e2e8f0; border-top-color: #2563eb;
                border-radius: 50%; animation: samSpin .7s linear infinite;
                }
                @keyframes samSpin { to { transform: rotate(360deg); } }
                .sam-loading-txt { font-size: 13px; color: #94a3b8; font-weight: 500; }

                /* Footer */
                .sam-footer {
                padding: 16px 28px; border-top: 1px solid #f1f5f9; background: #fafbfc;
                display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 12px; flex-shrink: 0;
                }
                .sam-footer-note { display: flex; align-items: center; gap: 6px; font-size: 12px; color: #94a3b8; font-weight: 500; }
                .sam-footer-btns { display: flex; gap: 10px; }
                .sam-btn-cancel {
                font-family: 'Plus Jakarta Sans', sans-serif; font-size: 13.5px; font-weight: 600; color: #64748b;
                padding: 10px 20px; border-radius: 10px; border: 1.5px solid #e2e8f0; background: #fff;
                cursor: pointer; transition: all .15s;
                }
                .sam-btn-cancel:hover { background: #f1f5f9; border-color: #cbd5e1; color: #374151; }
                .sam-btn-save {
                display: inline-flex; align-items: center; gap: 7px;
                font-family: 'Plus Jakarta Sans', sans-serif; font-size: 13.5px; font-weight: 700; color: #fff;
                padding: 10px 22px; border-radius: 10px; border: none;
                background: linear-gradient(135deg, #1e3a8a, #2563eb);
                cursor: pointer; box-shadow: 0 4px 14px rgba(37,99,235,.3);
                transition: transform .15s, box-shadow .18s;
                }
                .sam-btn-save:hover { transform: translateY(-1px); box-shadow: 0 8px 24px rgba(37,99,235,.38); }
                .sam-btn-save:disabled { opacity: .5; cursor: not-allowed; transform: none; box-shadow: none; }
                .sam-btn-spinner {
                display: inline-block; width: 16px; height: 16px;
                border: 2px solid rgba(255,255,255,.35); border-top-color: #fff;
                border-radius: 50%; animation: samBtnSpin .65s linear infinite;
                }
                @keyframes samBtnSpin { to { transform: rotate(360deg); } }

                @media (max-width: 700px) {
                .st-header { flex-direction: column; align-items: flex-start; }
                .st-search-wrap { width: 100%; }
                .st-thead-th, .st-tbody-td { padding: 10px 12px; }
                .sam-panel, .sdm-panel { max-width: 100%; }
                .sam-field-row { flex-direction: column; }
                }
            `}</style>
        
        </div>
    )
}
export default ScheduleSettlementTable;